// ============================================================
// GDPR-COMPLIANT LOCAL-FIRST ANALYTICS SERVICE
// ============================================================

const STORAGE_KEY = 'helpbook_analytics_events';

export const analyticsService = {
  // Track a custom user event
  trackEvent: (category, action, label = '', value = null) => {
    try {
      const events = analyticsService.getEvents();
      
      const newEvent = {
        id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        category, // e.g. 'navigation', 'marketplace', 'community', 'chat', 'onboarding'
        action,   // e.g. 'view_page', 'like_post', 'create_item', 'sent_message', 'start_tour'
        label,    // e.g. 'profile', 'iphone-12', 'suche-umzugshilfe'
        value,    // e.g. price, count
        timestamp: new Date().toISOString()
      };
      
      events.push(newEvent);
      
      // Limit storage to last 500 events to prevent growth
      if (events.length > 500) {
        events.shift();
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`📊 [Analytics] Event tracked:`, newEvent);
      }
    } catch (e) {
      console.error('Failed to track analytics event:', e);
    }
  },

  // Get all stored events
  getEvents: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Failed to parse analytics events:', e);
      return [];
    }
  },

  // Clear all events
  clearEvents: () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Failed to clear analytics events:', e);
    }
  },

  // Get detailed statistical summaries for the dashboard
  getStatsSummary: () => {
    const events = analyticsService.getEvents();
    const now = new Date();
    
    // Page views counter
    const pageViews = events.filter(e => e.action === 'view_page');
    
    // Page counts breakdown
    const pagesBreakdown = {};
    pageViews.forEach(e => {
      pagesBreakdown[e.label] = (pagesBreakdown[e.label] || 0) + 1;
    });
    
    // Active interactions (likes, sends, posts)
    const interactions = events.filter(e => e.action !== 'view_page');
    
    // Message counts
    const messagesSent = events.filter(e => e.action === 'sent_message').length;
    
    // Items created/favorited
    const itemsCreated = events.filter(e => e.action === 'create_item').length;
    const itemsFavorited = events.filter(e => e.action === 'favorite_item').length;
    
    // Posts liked/created
    const postsCreated = events.filter(e => e.action === 'create_post').length;
    const postsLiked = events.filter(e => e.action === 'like_post').length;

    // Engagement score (arbitrary calculation)
    const score = (pageViews.length * 2) + (messagesSent * 5) + (itemsCreated * 10) + (postsCreated * 10) + (postsLiked * 3) + (itemsFavorited * 3);

    return {
      totalPageViews: pageViews.length,
      totalInteractions: interactions.length,
      pagesBreakdown,
      messagesSent,
      itemsCreated,
      itemsFavorited,
      postsCreated,
      postsLiked,
      engagementScore: Math.min(100, score), // caps at 100
      level: Math.floor(score / 50) + 1,
      nextLevelXP: (Math.floor(score / 50) + 1) * 50,
      currentXP: score % 50
    };
  }
};

export default analyticsService;
